import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import numeral from 'numeral';
import dayjs from 'dayjs';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';

import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import {
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { today } from '../../../../helpers/dates';
import { chartColor } from '../../../../styles/muiTheme';
import { getDeviceTimeseriesAPI } from '../../../../api';
import { compileChartData, prepareCsvData } from '../prep-data';
import SelectRange from '../../selectors/SelectRange';
import SelectTimezone, { TIMEZONES } from '../../selectors/SelectTimezone';
import GenerateCSVButton from '../../buttons/GenerateCSVButton';
import SelectAttributes from '../../selectors/SelectAttributes';
import Loader from '../../../Loaders/ComponentLoader';
import InfoButton from '../../buttons/InfoButton';
import GeneralTooltip from '../../tooltips/GeneralTooltip';

const ATTRIBUTES = [
  'DevType',
  'DataModel',
  'Radiation',
  'PanelTemp',
  'VSupply',
  'ErrorCode',
  'CommError',
  'OperMode',
  'Status',
];

function SensorChart({ site, sensor }) {
  const [loading, setLoading] = useState(false);
  const [timezone, setTimezone] = useState(TIMEZONES[0]);
  const [range, setRange] = useState({ key: '', name: '', start: 0, end: 0 });
  const [attributes, setAttrbutes] = useState([ATTRIBUTES[2]]);
  const [rawData, setRawData] = useState([]);
  const [chartData, setChartData] = useState([]);

  // set timezone
  useEffect(() => {
    if (!isEmpty(site.timezone)) {
      setTimezone(site.timezone);
    }
  }, [site.timezone]);

  // set range
  useEffect(() => {
    if (!isEmpty(site)) {
      setRange(today(site.timezone.offset));
    }
  }, [site]);

  // set chartData
  useEffect(() => {
    if (!isEmpty(rawData)) {
      setChartData(compileChartData(rawData));
    }
  }, [site, sensor, range, rawData]);

  // set default chart data
  useEffect(() => {
    if (isEmpty(rawData) && range.label) {
      fetchChartData(range);
    }
    /* eslint-disable-next-line */
  }, [rawData, range]);

  const fetchChartData = async (range) => {
    setLoading(true);

    const payload = await getDeviceTimeseriesAPI(
      sensor.sensor_id,
      range.start,
      range.end,
      attributes
    );

    setRawData(payload);
    setLoading(false);
  };

  const handleRangeSelect = (newRange) => {
    setRange(newRange);
    fetchChartData(newRange);
  };

  return (
    <>
      <Grid item xs={12}>
        <Card raised>
          <CardHeader
            sx={{ '& .MuiCardHeader-content': { display: 'flex' }, p: 1 }}
            title='Sensor Chart'
            action={
              <Stack
                direction='row'
                mr={2}
                spacing={2}
                justifyContent='flex-end'
                divider={
                  <Divider
                    orientation='vertical'
                    flexItem
                    sx={{ mt: 1, mb: 1 }}
                  />
                }>
                <SelectAttributes
                  selectedAttributes={attributes}
                  setSelectedAttributes={setAttrbutes}
                  attributes={ATTRIBUTES}
                />

                <SelectRange
                  range={range}
                  handleRangeSelect={handleRangeSelect}
                  timezone={timezone}
                />

                <SelectTimezone
                  selectedTimezone={timezone}
                  setSelectedTimezone={setTimezone}
                />

                <Button
                  color='primary'
                  variant='contained'
                  size='small'
                  onClick={() => fetchChartData(range)}>
                  Generate
                </Button>

                <GenerateCSVButton
                  generateCsvString={() =>
                    prepareCsvData(
                      chartData,
                      attributes,
                      `_S${sensor.parent_index}`
                    )
                  }
                  filename={`${sensor.name} Data (${dayjs().format(
                    'ddd MMM DD YYYY'
                  )})`}
                />

                <InfoButton
                  title='Sensor Chart'
                  content={
                    <>
                      <Typography variant='body2' color='text.secondary'>
                        Select attributes, date range, and timezone.
                      </Typography>
                      <Typography variant='body2' color='text.secondary'>
                        Click "Generate" to fetch chart data and generate a
                        chart.
                      </Typography>
                      <Typography variant='body2' color='text.secondary'>
                        Click Download icon to download the data shown in the
                        chart.
                      </Typography>
                    </>
                  }
                />
              </Stack>
            }
          />
        </Card>
      </Grid>

      {isEmpty(chartData) && !loading ? null : (
        <Grid item xs={12}>
          <Card raised>
            <CardContent
              sx={{
                flex: '1 1 auto',
                overflowY: 'auto',
                minHeight: '0px',
                width: '100%',
                height: '100%',
              }}>
              {loading ? (
                <div style={{ height: '600px' }}>
                  <Loader height={150} width={150} />
                </div>
              ) : (
                <ResponsiveContainer
                  key={`sensor-chart-${sensor.sensor_id}`}
                  width={'100%'}
                  height={600}
                  style={{
                    marginBottom: 2,
                    '& .recharts-surface': { overflow: 'visible' },
                  }}>
                  <LineChart
                    width={'100%'}
                    height={250}
                    data={chartData}
                    margin={{
                      top: 5,
                      right: 5,
                      left: 0,
                      bottom: 250,
                    }}>
                    <YAxis
                      domain={[
                        (dataMin) => Math.min(0, dataMin),
                        (dataMax) => Math.ceil(dataMax / 10) * 10,
                      ]}
                      tickFormatter={(val, _axis) => {
                        return numeral(val.toPrecision(4)).format('0,0.[000]');
                      }}
                    />
                    <XAxis
                      type='number'
                      tickMargin={10}
                      dataKey='timestamp'
                      domain={[range.start / 1000, range.end / 1000]}
                      tickFormatter={(unixTime) => {
                        return dayjs(unixTime * 1000)
                          .utcOffset(get(site, 'timezone.offset'))
                          .format('MM/DD h:mm A');
                      }}
                    />

                    <Tooltip
                      wrapperStyle={{
                        visibility: 'visible',
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                      }}
                      position={{ x: 0, y: 360 }}
                      content={<GeneralTooltip timezone={site.timezone} />}
                    />

                    {map(attributes, (attribute, idx) => {
                      let key = attribute + '_S' + sensor.parent_index;
                      return (
                        <Line
                          dot={false}
                          key={key}
                          type='monotone'
                          dataKey={key}
                          stroke={chartColor(idx)}
                          connectNulls
                        />
                      );
                    })}
                  </LineChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      )}
    </>
  );
}

SensorChart.propTypes = {
  site: PropTypes.object,
  sensor: PropTypes.object,
};

export default SensorChart;
